import { useSyncExternalStore } from 'react';

/**
 * Who is currently "acting" on this device — the tapped avatar.
 *
 * This is an authorship label, never a permission boundary: the household passcode is the
 * only real gate. It is per-device (the wall resets to nobody; a phone remembers you), so
 * it lives in localStorage rather than on the server.
 */

const KEY = 'pacer.activeMember';

let current: string | null = readInitial();
const listeners = new Set<() => void>();

function readInitial(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null; // private mode / storage disabled
  }
}

function emit(): void {
  for (const l of listeners) l();
}

export function setActiveMember(id: string | null): void {
  current = id;
  try {
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
  } catch {
    /* non-persistent is still usable for this session */
  }
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Keep multiple tabs on the same tablet in sync.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) {
      current = e.newValue;
      emit();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function useActiveMemberId(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => null,
  );
}
